// Photos for the Play screen cards, taken in the real game: tracks (helicopter shot), atmospheres (chase
// camera), item effects (staged by the roadside) and arcade games (a frame of a simulated play).
// One photo at a time, when the browser is idle, then kept in IndexedDB: redone only if what it shows changes. While it
// is redone (the staging changed, the player picked another atmosphere or outfit), the previous photo of the same
// subject stands in, so a card the player has seen before never goes back to its pictogram.
import type { TrackDef } from '@gruvetto/core';
import { gameShot } from '../../games/host.js';
import { db } from '../../data/db.js';
import { state } from '../../data/store.js';
import { catalog, themeContent } from '../../data/creations.js';
import type { ItemStage } from './itemStage.js';
import { softwareRendering } from '../../render/gpu.js';
import { photosWanted } from './photoPolicy.js';

const W = 1280, H = 600, VERSION = 9;
/** Photos kept: every track in every atmosphere, the items in each, the games, the portraits of the riders met in the standings, with room for the player's creations. */
export const KEEP = 240;
/** Shot formats: cards, and wide banners (event page hero) that stay sharp across the page width. */
export const SIZES = { card: [W, H], wide: [1920, 720], share: [1440, 960] } as const;
export type PhotoSize = keyof typeof SIZES;
/**
 * A photo to take. `key` names exactly what it shows (any change means a new photo); `subject` names what it is a photo
 * of (this track at this size, this item, the portrait), the same whatever the atmosphere or the staging: the newest
 * photo of the subject stands in while the exact one is missing.
 */
export interface PhotoJob { key: string; subject: string; run: () => Promise<Blob> }
interface Queued extends PhotoJob { root: HTMLElement }
/** A cached photo. Rows written before subjects existed have none: they are shown by key and never stand in. */
interface Row { key: string; subject?: string; at: number; blob: Blob }

const urls = new Map<string, string>();
const failed = new Set<string>(), tries = new Map<string, number>();
let queue: Queued[] = [];
let running = false, release: ReturnType<typeof setTimeout> | undefined;

function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}
/** Cache key: everything visible in the photo, plus the staging version. */
const keyOf = (...parts: unknown[]) => 'p' + hash(JSON.stringify([VERSION, ...parts]));
/** Cache key for photos taken outside this module (rider portrait). */
export const photoKey = keyOf;
const rider = () => [state.profile.color, state.profile.look];

// ---------- what the cache holds ----------
/** Every cached photo, by key, and the newest of each subject: read from the database once, kept up to date here. */
const index = new Map<string, { subject: string | undefined; at: number }>(), newest = new Map<string, string>();
let indexed: Promise<void> | null = null;
const indexReady = () => (indexed ??= db.all<Row>('photos').then(rows => { for (const r of rows) remember(r.key, r.subject, r.at); }).catch(() => {}));
function remember(key: string, subject: string | undefined, at: number) {
  index.set(key, { subject, at });
  if (!subject) return;
  const cur = newest.get(subject);
  if (!cur || (index.get(cur)?.at ?? 0) <= at) newest.set(subject, key);
}
/** The cached photo that stands in for `job` while its own is missing: the newest of the same subject, when there is one. */
const standIn = (job: PhotoJob): string | null => { const k = newest.get(job.subject); return k && k !== job.key && !urls.has(job.key) ? k : null; };
/** Forgets everything shown and indexed this session (tests). */
export function resetPhotos() { urls.clear(); failed.clear(); tries.clear(); queue = []; index.clear(); newest.clear(); indexed = null; }

/** The object URL of a photo shown this session or recalled from the database (recallPhotos); undefined otherwise. */
export const photoUrl = (key: string): string | undefined => urls.get(key);
/**
 * Brings the cached photos of `jobs` into this session, taking none: a view redrawn too often to hold slots (the race
 * board) reads them with photoUrl. A photo never taken stays absent until a screen with slots asks for it.
 */
export async function recallPhotos(jobs: PhotoJob[]): Promise<void> {
  await indexReady();
  for (const { key } of jobs) {
    if (urls.has(key) || !index.has(key)) continue;
    const saved = await db.get<Row>('photos', key).catch(() => undefined);
    if (saved?.blob && !urls.has(key)) urls.set(key, URL.createObjectURL(saved.blob));
  }
}

/** The 3D studio (three.js, the renderer): fetched the first time a photo has to be taken, never for cached ones. */
type Studio = typeof import('./photoStudio.js');
let studioMod: Studio | null = null;
const studio = (): Promise<Studio> => (studioMod ? Promise.resolve(studioMod) : import('./photoStudio.js').then(m => (studioMod = m)));

// toBlob copies the image right away: the WebGL buffer of the frame just drawn is still there
const blobOf = (c: HTMLCanvasElement) => new Promise<Blob>((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.85));

/** Staging of track photos: bumped when the spot or the framing changes (world2: never inside a tunnel). */
const TRACK_SHOT = 'world2';

/**
 * Track seen from a helicopter above its steepest climb (or its tightest bend), outside any tunnel. The riders are specks
 * from up there: the outfit is not part of the key, so a change of jersey does not redo every track.
 */
export function trackPhoto(def: TrackDef, themeId: string, size: PhotoSize = 'card'): PhotoJob {
  const world = catalog.get(def.id ?? '', 'track')?.creation.content.world ?? null;
  return {
    key: keyOf('track', def.pts, themeContent(themeId), size, world, TRACK_SHOT),
    subject: `track:${def.id ?? hash(JSON.stringify(def.pts))}:${size}`,
    run: async () => (await studio()).shootTrack(def, themeId, size),
  };
}

/** The two large frames of the share images (a click asks for them: no queue, no cache). Frees the 3D context afterwards. */
export async function sharePhotos(def: TrackDef, themeId: string): Promise<{ aerial: Blob; chase: Blob }> {
  const s = await studio();
  try { return { aerial: await s.shootTrack(def, themeId, 'share'), chase: await s.shootRider(def, themeId) }; } finally { s.release(); }
}

/** Atmosphere seen from the chase camera, on the reference track. */
export function themePhoto(themeId: string): PhotoJob {
  return { key: keyOf('theme', themeContent(themeId), rider()), subject: `theme:${themeId}`, run: async () => (await studio()).shootTheme(themeId) };
}

/** Effect of an item staged on the reference track, in the chosen atmosphere. */
export function itemPhoto(id: string, st: ItemStage, themeId: string): PhotoJob {
  return { key: keyOf('item', id, st, themeContent(themeId), rider()), subject: `item:${id}`, run: async () => (await studio()).shootItem(st, themeId) };
}

/** Frame of an arcade game after a few seconds of simulated play. */
export function gamePhoto(id: string, code: string): PhotoJob {
  // games draw in 16:9: the photo keeps that format, without black bars
  return { key: keyOf('game', id, hash(code)), subject: `game:${id}`, run: async () => blobOf(await gameShot(code, { width: 640, height: 360, seconds: 20, timeout: 15000 })) };
}

// ---------- display and queue ----------
/**
 * Slot of a photo in a card; `overlay` (pictogram) stays visible until the photo is ready. A photo of the same subject
 * already on screen this session stands in meanwhile (loadPhotos brings one from the database otherwise).
 */
export function photoSlot(job: PhotoJob, overlay = ''): string {
  const url = urls.get(job.key), old = standIn(job), stale = old && urls.get(old);
  const img = url ? `<img src="${url}" alt="">` : stale ? `<img src="${stale}" alt="" data-stale>` : '';
  return `<span class="photo${img ? ' ready' : ''}" data-photo="${job.key}">${img}${overlay}</span>`;
}

/** Puts the photo of `key` in its slots. `stale`: the stand-in, placed only where no photo shows yet. */
function show(key: string, stale = false) {
  const url = urls.get(key);
  if (!url) return;
  for (const slot of document.querySelectorAll<HTMLElement>(`[data-photo="${key}"]`)) {
    // the overlay may hold its own image (fallback swatch): only the photo is a direct child
    const own = slot.querySelector<HTMLImageElement>(':scope > img:not([data-stale])'), old = slot.querySelector<HTMLImageElement>(':scope > img[data-stale]');
    if (own || (stale && old)) continue;
    // the fresh photo replaces its stand-in in place: the card does not blink
    if (old) { old.src = url; old.removeAttribute('data-stale'); continue; }
    const img = document.createElement('img');
    img.alt = '';
    img.onload = () => slot.classList.add('ready');
    img.src = url;
    if (stale) img.dataset.stale = '';
    slot.prepend(img);
  }
}

/** Shows the stand-in of `job` in its slots, read from the database when it was not shown yet this session. */
async function showStandIn(job: Queued) {
  const old = standIn(job);
  if (!old) return;
  if (!urls.has(old)) {
    const saved = await db.get<Row>('photos', old).catch(() => undefined);
    if (!saved?.blob || urls.has(job.key)) return;
    urls.set(old, URL.createObjectURL(saved.blob));
  }
  for (const slot of document.querySelectorAll<HTMLElement>(`[data-photo="${job.key}"]`)) {
    if (slot.querySelector(':scope > img')) continue;
    const img = document.createElement('img');
    img.alt = ''; img.dataset.stale = '';
    img.onload = () => slot.classList.add('ready');
    img.src = urls.get(old)!;
    slot.prepend(img);
  }
}

const alive = (root: HTMLElement) => root.isConnected && root.getClientRects().length > 0;

/** Fills the slots of `root`, in the given order. A new request for the same `root` replaces the previous one. */
export function loadPhotos(root: HTMLElement, jobs: PhotoJob[]) {
  queue = queue.filter(q => q.root !== root && alive(q.root));
  for (const j of jobs) {
    if (urls.has(j.key)) { show(j.key); continue; }
    if (failed.has(j.key) || queue.some(q => q.key === j.key)) continue;
    queue.push({ ...j, root });
  }
  void run();
}

async function run() {
  if (running) return;
  running = true;
  clearTimeout(release);
  try {
    await indexReady();
    while (queue.length) {
      const job = queue.shift()!;
      if (!alive(job.root) || urls.has(job.key)) continue;
      const saved = index.has(job.key) ? await db.get<Row>('photos', job.key).catch(() => undefined) : undefined;
      if (saved?.blob) { urls.set(job.key, URL.createObjectURL(saved.blob)); show(job.key); continue; }
      // the previous photo of the subject stands in right away, whether or not a new one can be taken on this machine
      await showStandIn(job);
      // photos already taken are still shown; new ones only when this machine can afford them (setting "Card photos").
      // Not marked as failed: switching the setting on takes them at the next visit.
      if (!photosWanted(state.settings.photos, softwareRendering())) continue;
      // building a world blocks the main thread for a few hundred milliseconds: wait for a quiet moment
      await new Promise<void>(res => (typeof requestIdleCallback === 'function' ? requestIdleCallback(() => res(), { timeout: 600 }) : setTimeout(res, 60)));
      if (!alive(job.root)) continue;
      try {
        // User Timing entry: the cost of each photo is visible in DevTools and measured by the e2e journey
        const t0 = performance.now();
        const blob = await job.run();
        performance.measure('gv-photo', { start: t0, detail: job.key });
        urls.set(job.key, URL.createObjectURL(blob));
        show(job.key);
        await store(job, blob);
      } catch (e) {
        // a game is slow when the machine is busy: a second chance at the end of the queue; after that (no WebGL,
        // crashing game) the card keeps its pictogram
        const n = (tries.get(job.key) ?? 0) + 1;
        tries.set(job.key, n);
        if (n < 2) queue.push(job);
        else { failed.add(job.key); console.warn('Photo impossible', e); }
      }
    }
  } finally {
    running = false;
    // the off-screen view holds a WebGL context: release it when nothing is requested any more
    release = setTimeout(() => { if (!running) studioMod?.release(); }, 4000);
  }
}

async function store({ key, subject }: PhotoJob, blob: Blob) {
  try {
    const at = Date.now();
    await db.put('photos', { key, subject, blob, at } satisfies Row);
    remember(key, subject, at);
    // the oldest photos go first; a subject's stand-in is never the one dropped while a newer one of it exists
    const old = [...index].sort((a, b) => a[1].at - b[1].at).slice(0, Math.max(0, index.size - KEEP));
    for (const [k, { subject: s }] of old) {
      await db.del('photos', k);
      index.delete(k);
      if (s && newest.get(s) === k) newest.delete(s);
    }
  } catch { /* the cache is a convenience: the photo stays displayed for this session */ }
}
